import { FunctionComponent } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useParams, useLocation } from "wouter-preact";
import { useStore } from "@nanostores/preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isLogined, $isSubAccount } from "../../store/user_info.ts";
import { gotoLoginPage, navigate } from "../../router_utils.ts";
import { requestDelete, requestGet, requestPost, requestWithParams } from "../../utils/request.ts";
import { useConfirm } from "../../components/dialog/dialog.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import { refreshUserInfo } from "../../store/user_info.ts";
import { invalidateOSSInfo } from "../../store/oss.ts";
import { invalidateCameraInfo } from "../../store/cameras.ts";
import { usePageRefresh, useRequestScope } from "../../hooks/use_page_refresh.ts";

type OSSDetail = {
    oss_id: number;
    oss_name: string;
    oss_access_id: string;
    oss_access_key: string;
    oss_endpoint: string;
    oss_bucket: string;
    paid: boolean;
    vip_expire?: number;
};

export const OSSManagerPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const confirm = useConfirm();
    const params = useParams();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const [location, _setLocation] = useLocation();

    // Use refs to break dependency cycles
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const idParam = params["id"] ?? "new";
    const isCreate = idParam === "new" || idParam === "undefined"; // Handle undefined string
    const ossId = isCreate ? -1 : Number.parseInt(idParam);

    const [isBusy, setIsBusy] = useState(false);
    const [dirty, setDirty] = useState(false);
    const scope = useRequestScope(`oss-form:${idParam}`);
    // Use Refs for form inputs to avoid re-renders during typing (Uncontrolled Components)
    const ossNameRef = useRef<HTMLInputElement>(null);
    const accessIdRef = useRef<HTMLInputElement>(null);
    const accessKeyRef = useRef<HTMLInputElement>(null);
    const endpointRef = useRef<HTMLInputElement>(null);
    const bucketRef = useRef<HTMLInputElement>(null);

    const [paid, setPaid] = useState(false); // Only state that affects layout structure
    const [vipExpire, setVipExpire] = useState<number | undefined>(undefined);

    // Use a ref to track if component is mounted
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => { isMountedRef.current = false; };
    }, []);

    useEffect(() => {
        document.title = isCreate ? t({ zh: "新增存储", en: "New Storage" }) : t({ zh: "存储管理", en: "Storage" });
    }, [t, isCreate]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? (isCreate ? "/oss/new" : `/oss/${ossId}`));
        } else if (isSubAccount) {
            toast(t({ zh: "子账户无权访问此页面", en: "Access Denied" }), "error");
            navigate("/overview", { replace: true });
        }
    }, [isLogined, location, isCreate, ossId, isSubAccount, toast, t]);

    const load = useMemo(() => async () => {
        if (!isLogined || isSubAccount) return;
        if (isCreate) {
            // Reset refs
            if (ossNameRef.current) ossNameRef.current.value = "";
            if (accessIdRef.current) accessIdRef.current.value = "";
            if (accessKeyRef.current) accessKeyRef.current.value = "";
            if (endpointRef.current) endpointRef.current.value = "";
            if (bucketRef.current) bucketRef.current.value = "";
            setPaid(false);
            setVipExpire(undefined);
            setDirty(false);
            return;
        }
        const request = scope.begin();
        try {
            setIsBusy(true);
            const resp = await requestGet<OSSDetail>("/v2/oss_detail", { oss_id: ossId }, request.signal);
            request.check();
            if (!isMountedRef.current) return;
            
            // Update refs directly
            if (ossNameRef.current) ossNameRef.current.value = resp.oss_name ?? "";
            if (accessIdRef.current) accessIdRef.current.value = resp.oss_access_id ?? "";
            if (accessKeyRef.current) accessKeyRef.current.value = resp.oss_access_key ?? "";
            if (endpointRef.current) endpointRef.current.value = resp.oss_endpoint ?? "";
            if (bucketRef.current) bucketRef.current.value = resp.oss_bucket ?? "";
            
            setPaid(!!resp.paid);
            setVipExpire(resp.vip_expire);
            setDirty(false);
        } catch (e) {
            if (!isMountedRef.current || !request.current()) return;
            toastRef.current((e as Error)?.message ?? "Request Failed", "error");
            throw e;
        } finally {
            request.finish();
            if (isMountedRef.current && request.current()) setIsBusy(false);
        }
    }, [isLogined, isSubAccount, isCreate, ossId, scope]);

    const refresh = usePageRefresh({
        pageKey: `oss-form:${idParam}`, enabled: isLogined && !isSubAccount, canAutoRefresh: !dirty && !isBusy,
        refresh: async () => {
            setIsBusy(true);
            try { await refreshUserInfo(); await load(); } finally { setIsBusy(false); }
        },
    });
    const reload = () => {
        if (!dirty || globalThis.confirm(t({ zh: "重新加载将丢弃未保存修改，继续？", en: "Discard unsaved changes and reload?" }))) {
            void refresh.refreshNow();
        }
    };
    const invalidateLists = () => { invalidateOSSInfo(); invalidateCameraInfo(); };

    const saveAction = useMemo(() => async () => {
        // Read values directly from DOM refs
        const ossName = ossNameRef.current?.value ?? "";
        const accessId = accessIdRef.current?.value ?? "";
        const accessKey = accessKeyRef.current?.value ?? "";
        const endpoint = endpointRef.current?.value ?? "";
        const bucket = bucketRef.current?.value ?? "";
        
        if (ossName.trim().length <= 0) {
            toastRef.current(t({ zh: "请输入存储名称", en: "Please input storage name" }), "warn");
            return;
        }
        try {
            if (!isMountedRef.current) return;
            setIsBusy(true);
            if (isCreate) {
                const resp = await requestPost<OSSDetail>("/v2/oss", {
                    oss_name: ossName.trim(),
                    oss_access_id: accessId.trim(),
                    oss_access_key: accessKey.trim(),
                    oss_endpoint: endpoint.trim(),
                    oss_bucket: bucket.trim(),
                });
                if (!isMountedRef.current) return;
                invalidateLists();
                setDirty(false);
                toastRef.current(t({ zh: "新增成功", en: "Created" }), "success");
                navigate(`/oss/${resp.oss_id}`, { replace: true });
            } else {
                await requestWithParams("PATCH", "/v2/oss", {
                    oss_id: ossId,
                    oss_name: ossName.trim(),
                    oss_access_id: accessId.trim(),
                    oss_access_key: accessKey.trim(),
                    oss_endpoint: endpoint.trim(),
                    oss_bucket: bucket.trim(),
                });
                if (!isMountedRef.current) return;
                invalidateLists();
                setDirty(false);
                toastRef.current(t({ zh: "保存成功", en: "Saved" }), "success");
                await load().catch(e => toastRef.current(t({ zh: "保存成功，刷新失败：", en: "Saved; refresh failed: " }) + e.message, "warn"));
            }
        } catch (e) {
            if (!isMountedRef.current) return;
            toastRef.current((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            if (isMountedRef.current) setIsBusy(false);
        }
    }, [isCreate, ossId, t, load]); // Dependency list is now minimal and stable

    const deleteAction = useMemo(() => async () => {
        const ok = await confirm(t({ zh: "确认删除该存储配置？此操作不可恢复。", en: "Delete this storage config? This cannot be undone." }));
        if (!ok) return;
        try {
            setIsBusy(true);
            await requestDelete("/v2/oss", { oss_id: ossId });
            invalidateLists();
            toastRef.current(t({ zh: "删除成功", en: "Deleted" }), "success");
            navigate("/oss", { replace: true });
        } catch (e) {
            toastRef.current((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [ossId, confirm, t]);

    const checkAction = useMemo(() => async () => {
        if (isCreate) return;
        try {
            setIsBusy(true);
            await requestPost("/v2/oss/cors", { oss_id: ossId });
            toastRef.current(t({ zh: "连接成功并已设置CORS规则", en: "Connection successful & CORS rules set" }), "success");
        } catch (e) {
            toastRef.current((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [ossId, isCreate, t]);

    const vipExpireInfo = useMemo(() => {
        if (!paid) return null;
        if (!vipExpire || vipExpire <= 0) return null;
        const expireMs = vipExpire * 1000;
        const expired = expireMs <= Date.now();
        const d = new Date(expireMs);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const dateText = `${y}-${m}-${day}`;
        return { expired, dateText };
    }, [paid, vipExpire]);

    const disableDelete = !isCreate && paid && vipExpireInfo && !vipExpireInfo.expired;

    if (!isLogined) {
        return (
            <div>
                <article aria-busy="true" class="container"></article>
            </div>
        );
    }

    return (
        <div>
            <div class="farpov_top_container">
                {refresh.error && <p role="alert">{refresh.error}</p>}
                {refresh.pendingRefresh && <p>{t({ zh: "页面已恢复，未保存修改已保留", en: "Page resumed; unsaved changes retained" })}</p>}
                {(refresh.pendingRefresh || refresh.error) && (
                    <button class="outline" disabled={isBusy || refresh.refreshing} onClick={reload}>{t({ zh: "重新加载", en: "Reload data" })}</button>
                )}
                <form onSubmit={(e) => e.preventDefault()} onInputCapture={() => setDirty(true)} onChangeCapture={() => setDirty(true)}>
                    <label>
                        {t({ zh: "名称", en: "Name" })}
                        <input
                            type="text"
                            ref={ossNameRef}
                            disabled={isBusy}
                        />
                    </label>
                    <label>
                        {t({ zh: "AccessKey ID", en: "AccessKey ID" })}
                        <input
                            type="text"
                            ref={accessIdRef}
                            disabled={isBusy || (!isCreate && paid)}
                        />
                    </label>
                    <label>
                        {t({ zh: "AccessKey Secret", en: "AccessKey Secret" })}
                        <input
                            type="password"
                            ref={accessKeyRef}
                            disabled={isBusy || (!isCreate && paid)}
                        />
                    </label>
                    <label>
                        {t({ zh: "Endpoint", en: "Endpoint" })}
                        <input
                            type="text"
                            ref={endpointRef}
                            disabled={isBusy || (!isCreate && paid)}
                        />
                    </label>
                    <label>
                        {t({ zh: "Bucket", en: "Bucket" })}
                        <input
                            type="text"
                            ref={bucketRef}
                            disabled={isBusy || (!isCreate && paid)}
                        />
                    </label>
                    {!isCreate ? (
                        <small>
                            {paid ? t({ zh: "该存储处于付费状态，部分字段不可修改。", en: "This storage is paid; some fields are locked." }) : ""}
                        </small>
                    ) : null}
                    <div class="grid" style="margin-top: var(--pico-spacing);">
                        <button type="submit" disabled={isBusy} onClick={saveAction} style={{ marginBottom: 0 }}>
                            {isBusy ? <span aria-busy="true"></span> : t({ zh: "保存", en: "Save" })}
                        </button>
                        <button type="button" class="secondary" disabled={isBusy} onClick={() => navigate("/oss", { replace: false })} style={{ marginBottom: 0 }}>
                            {t({ zh: "返回", en: "Back" })}
                        </button>
                    </div>
                    {!isCreate ? (
                        <div style={{ marginTop: "var(--pico-spacing)" }}>
                             <button type="button" class="outline" disabled={isBusy} onClick={checkAction}>
                                {isBusy ? <span aria-busy="true"></span> : t({ zh: "检查连接并初始化 (CORS)", en: "Check Connection & Init (CORS)" })}
                            </button>
                        </div>
                    ) : null}
                    {!isCreate ? (
                        <>
                            <hr />
                            <button type="button" class="contrast" disabled={isBusy || !!disableDelete} onClick={deleteAction}>
                                {isBusy ? <span aria-busy="true"></span> : t({ zh: "删除", en: "Delete" })}
                            </button>
                            {disableDelete ? (
                                <small>{t({ zh: "付费服务未到期，禁止删除。", en: "Paid service not expired; delete is disabled." })}</small>
                            ) : null}
                        </>
                    ) : null}
                </form>
            </div>
        </div>
    );
};

export default OSSManagerPage;
